window.AdminHomeFeatures = function AdminHomeFeatures({ showNotification }) {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [activeSubTab, setActiveSubTab] = React.useState('scope'); // scope, stats, why
  const [mediaPickerOpen, setMediaPickerOpen] = React.useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = React.useState(null);
  
  // Default Data Structures
  const [featuresData, setFeaturesData] = React.useState({
    engineeringScope: [
      {
        id: "scope-1",
        title: "Custom Test Rigs",
        icon: "speed",
        description: "Multi-axis electro-hydraulic and dynamic fatigue testing rigs for structural components, chassis, and heavy mechanical assemblies.",
        linkText: "Explore Test Rigs",
        linkTo: "solutions:industrial-testing"
      },
      {
        id: "scope-2",
        title: "Automated Test Benches",
        icon: "tune",
        description: "High-throughput pneumatic, hydraulic, and electrical validation benches with automated clamping and micro-leakage detection.",
        linkText: "Explore Test Benches",
        linkTo: "solutions:industrial-testing"
      },
      {
        id: "scope-3",
        title: "Railway Testing Systems",
        icon: "train",
        description: "RDSO & UIC compliant test rigs for brake valves, TBUs, pantograph contact force simulation, and bogie dynamic endurance.",
        linkText: "Railway Systems",
        linkTo: "industries:railway"
      },
      {
        id: "scope-4",
        title: "Industrial Automation",
        icon: "precision_manufacturing",
        description: "Turnkey 6-axis robotic welding cells, automated assembly lines, optical 3D vision guidance, and smart hydraulic clamping fixtures.",
        linkText: "Automation Capabilities",
        linkTo: "solutions:manufacturing-automation"
      },
      {
        id: "scope-5",
        title: "Special Purpose Machines",
        icon: "settings",
        description: "Bespoke machinery engineered for specific multi-stage manufacturing, precision bushing insertion, pressing, and laser marking.",
        linkText: "View SPM Solutions",
        linkTo: "solutions:manufacturing-automation"
      },
      {
        id: "scope-6",
        title: "DAQ & Test Software",
        icon: "terminal",
        description: "High-speed 100 kHz data acquisition software, real-time tolerance bands, and automated tamper-proof PDF compliance reports.",
        linkText: "Test Software Suites",
        linkTo: "solutions:daq-software"
      }
    ],
    stats: [
      { id: "stat-1", number: "10+", label: "Years Experience", spanTwo: false },
      { id: "stat-2", number: "250+", label: "Projects Delivered", spanTwo: false },
      { id: "stat-3", number: "4", label: "Core Sectors", spanTwo: false },
      { id: "stat-4", number: "100+", label: "Custom Machines", spanTwo: false },
      { id: "stat-5", number: "45+", label: "Industrial Partners", spanTwo: true }
    ],
    whyNovatek: [
      {
        id: "why-1",
        title: "Complete Engineering Under One Roof",
        icon: "home_work",
        description: "Mechanical + Electrical + Fluid Power + PLC Automation + Instrumentation + Software all executed in-house."
      },
      {
        id: "why-2",
        title: "Custom Engineered To Your Spec",
        icon: "design_services",
        description: "No rigid catalog compromises. Every fixture, sensor range, and test recipe is built around your specific component."
      },
      {
        id: "why-3",
        title: "Test & Data Traceability",
        icon: "analytics",
        description: "Automated test sequences, calibrated sensor logging, SQL database storage, and instant tamper-proof digital certificates."
      },
      {
        id: "why-4",
        title: "Concept to Commissioning",
        icon: "verified_user",
        description: "We take full responsibility: 3D CAD → Manufacturing → Assembly → FAT → Site Installation → Commissioning & Lifecycle Support."
      }
    ]
  });

  const [editingItem, setEditingItem] = React.useState(null); // { type, index, data }
  const [searchQuery, setSearchQuery] = React.useState('');
  const [dropdownOptions, setDropdownOptions] = React.useState({
    industries: [],
    categories: [],
    products: []
  });
  const [isCustomLink, setIsCustomLink] = React.useState(false);

  React.useEffect(() => {
    fetchFeatures();
    loadDropdownOptions();
  }, []);

  const loadDropdownOptions = async () => {
    try {
      const options = {
        industries: window.INDUSTRIES_DATA || [
          { id: 'railway', slug: 'railway', title: 'Railway Testing Systems & Rigs' },
          { id: 'automotive', slug: 'automotive', title: 'Automotive Test & Automation Systems' },
          { id: 'aerospace', slug: 'aerospace', title: 'Aerospace Test Systems & GSE' },
          { id: 'industrial', slug: 'industrial', title: 'Industrial Automation & SPM' },
          { id: 'rd', slug: 'rd', title: 'R&D & Academic Research Test Systems' }
        ],
        categories: [],
        products: []
      };

      try {
        const catRes = await window.apiClient.get('categories.php');
        if (catRes.success && catRes.data) {
          options.categories = catRes.data;
        }
      } catch (e) {
        options.categories = [
          { name: 'Railways' },
          { name: 'Industrial Automation' },
          { name: 'Aerospace' },
          { name: 'Automotive' },
          { name: 'Custom CAD & Fixtures' }
        ];
      }

      try {
        const prodRes = await window.apiClient.get('products.php?admin=1');
        if (prodRes.success && prodRes.data) {
          options.products = prodRes.data;
        }
      } catch (e) {}

      setDropdownOptions(options);
    } catch (err) {
      console.error(err);
    }
  };

  React.useEffect(() => {
    if (editingItem && editingItem.data && editingItem.type === 'engineeringScope') {
      const link = editingItem.data.linkTo || '';
      if (!link) {
        setIsCustomLink(false);
        return;
      }
      
      const [type, value] = link.split(':');
      let exists = false;
      if (type === 'industries') {
        const list = dropdownOptions.industries || [];
        exists = list.some(i => i.slug === value);
      } else if (type === 'products') {
        const list = dropdownOptions.categories || [];
        exists = list.some(c => c.name === value);
      } else if (type === 'product-detail') {
        const list = dropdownOptions.products || [];
        exists = list.some(p => p.slug === value);
      }
      
      setIsCustomLink(!exists);
    }
  }, [editingItem, dropdownOptions]);

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      const res = await window.apiClient.get('content.php');
      if (res.success && res.data && res.data.home_features) {
        setFeaturesData(prev => ({
          ...prev,
          ...res.data.home_features
        }));
      }
    } catch (e) {
      console.error(e);
      showNotification('Failed to load home features data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFeatures = async () => {
    try {
      setSaving(true);
      const res = await window.apiClient.post('content.php', {
        section_key: 'home_features',
        data: featuresData
      });
      if (res.success) {
        showNotification('Home Features saved successfully!');
        window.dispatchEvent(new Event('novatek_content_updated'));
      } else {
        throw new Error(res.message || 'Failed to save');
      }
    } catch (e) {
      showNotification(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMove = (type, index, direction) => {
    const list = [...featuresData[type]];
    const targetIdx = index + direction;
    if (targetIdx >= 0 && targetIdx < list.length) {
      [list[index], list[targetIdx]] = [list[targetIdx], list[index]];
      setFeaturesData({ ...featuresData, [type]: list });
    }
  };

  const handleDelete = (type, index) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      const list = [...featuresData[type]];
      list.splice(index, 1);
      setFeaturesData({ ...featuresData, [type]: list });
    }
  };

  const handleSaveEdit = () => {
    if (!editingItem) return;
    const { type, index, data } = editingItem;
    const list = [...featuresData[type]];
    if (index !== undefined) {
      list[index] = data;
    } else {
      list.push(data);
    }
    setFeaturesData({ ...featuresData, [type]: list });
    setEditingItem(null);
  };

  const openEditor = (type, index, data) => {
    setEditingItem({
      type,
      index,
      data: { ...data }
    });
  };

  const createNewItem = (type) => {
    const base = { id: `${type}-${Date.now()}` };
    if (type === 'stats') {
      openEditor(type, undefined, { ...base, number: '0', label: 'New Stat', spanTwo: false });
    } else {
      openEditor(type, undefined, { ...base, title: 'New Item', icon: 'star', description: '', linkText: 'Learn More', linkTo: '', image: '' });
    }
  };

  if (loading) {
    return <div className="p-10 text-center font-mono text-xs text-slate-500 animate-pulse">Loading features data...</div>;
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-20">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h2 className="font-sans text-xl font-bold text-slate-900">Homepage Features</h2>
          <p className="font-sans text-xs text-slate-500 mt-1">Manage the Core Scope, Stats, and 'Why Novatek' sections.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSaveFeatures}
            disabled={saving}
            className="bg-secondary hover:bg-orange-600 text-white font-mono text-xs font-bold uppercase tracking-widest px-6 py-2.5 rounded-lg transition-all shadow-md flex items-center gap-2 disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-sm">{saving ? 'sync' : 'save'}</span>
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>

      {/* Sub Tabs */}
      <div className="flex gap-2 p-1 bg-slate-200/50 rounded-xl overflow-x-auto">
        {[
          { id: 'scope', label: 'What We Engineer', icon: 'precision_manufacturing' },
          { id: 'stats', label: 'Stats Strip', icon: 'bar_chart' },
          { id: 'why', label: 'Why Novatek', icon: 'verified_user' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveSubTab(tab.id); setSearchQuery(''); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-xs font-bold transition-all whitespace-nowrap ${
              activeSubTab === tab.id 
                ? 'bg-white text-slate-900 shadow-sm' 
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
          >
            <span className="material-symbols-outlined text-base">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Render Lists */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-6 space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h3 className="font-sans text-lg font-bold text-slate-900">
            {activeSubTab === 'scope' && 'Core Engineering Scope Cards'}
            {activeSubTab === 'stats' && 'Measurable Proof Stats'}
            {activeSubTab === 'why' && 'The Engineering Advantage Cards'}
          </h3>
          <button
            onClick={() => createNewItem(activeSubTab === 'scope' ? 'engineeringScope' : activeSubTab === 'stats' ? 'stats' : 'whyNovatek')}
            className="text-xs font-mono font-bold text-secondary hover:text-orange-700 flex items-center gap-1"
          >
            <span className="material-symbols-outlined text-sm">add</span> Add New
          </button>
        </div>

        {/* Filter and Search Bar */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="relative w-full sm:w-80">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base">search</span>
            <input
              type="text"
              placeholder={`Search ${activeSubTab === 'scope' ? 'features' : activeSubTab === 'stats' ? 'stats' : 'advantages'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs outline-none focus:border-secondary transition-colors"
            />
          </div>
        </div>

        <div className="space-y-3">
          {(() => {
            const originalList = (activeSubTab === 'scope' ? featuresData.engineeringScope : activeSubTab === 'stats' ? featuresData.stats : featuresData.whyNovatek) || [];
            const filteredList = originalList.filter(item => {
              if (!item) return false;
              const query = searchQuery.toLowerCase();
              return (
                (item.title || '').toLowerCase().includes(query) ||
                (item.description || '').toLowerCase().includes(query) ||
                (item.label || '').toLowerCase().includes(query) ||
                (item.number || '').toLowerCase().includes(query)
              );
            });
            return (
              <>
                {filteredList.map((item) => {
                  const originalIdx = originalList.indexOf(item);
                  return (
                    <div key={item.id} className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 hover:border-slate-300 bg-slate-50 transition-all group">
                      <div className="flex flex-col gap-1 text-slate-400">
                        <button onClick={() => handleMove(activeSubTab === 'scope' ? 'engineeringScope' : activeSubTab === 'stats' ? 'stats' : 'whyNovatek', originalIdx, -1)} className="hover:text-secondary disabled:opacity-30" disabled={originalIdx === 0}>
                          <span className="material-symbols-outlined text-base">expand_less</span>
                        </button>
                        <button onClick={() => handleMove(activeSubTab === 'scope' ? 'engineeringScope' : activeSubTab === 'stats' ? 'stats' : 'whyNovatek', originalIdx, 1)} className="hover:text-secondary disabled:opacity-30" disabled={originalIdx === originalList.length - 1}>
                          <span className="material-symbols-outlined text-base">expand_more</span>
                        </button>
                      </div>

                      {activeSubTab !== 'stats' && (
                        <div className="h-10 w-10 shrink-0 rounded-lg bg-orange-100 text-secondary flex items-center justify-center">
                          <span className="material-symbols-outlined text-xl">{item.icon}</span>
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <h4 className="font-sans text-sm font-bold text-slate-900 truncate">
                          {activeSubTab === 'stats' ? item.number : item.title}
                        </h4>
                        <p className="font-sans text-xs text-slate-500 truncate mt-0.5">
                          {activeSubTab === 'stats' ? item.label : item.description}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <button onClick={() => openEditor(activeSubTab === 'scope' ? 'engineeringScope' : activeSubTab === 'stats' ? 'stats' : 'whyNovatek', originalIdx, item)} className="p-2 text-slate-400 hover:text-secondary bg-white border border-slate-200 rounded-lg shadow-sm">
                          <span className="material-symbols-outlined text-sm">edit</span>
                        </button>
                        <button onClick={() => handleDelete(activeSubTab === 'scope' ? 'engineeringScope' : activeSubTab === 'stats' ? 'stats' : 'whyNovatek', originalIdx)} className="p-2 text-slate-400 hover:text-red-500 bg-white border border-slate-200 rounded-lg shadow-sm">
                          <span className="material-symbols-outlined text-sm">delete</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
                {originalList.length === 0 ? (
                  <div className="p-8 text-center text-slate-500 font-sans text-sm border-2 border-dashed border-slate-200 rounded-xl">
                    No items in this section.
                  </div>
                ) : filteredList.length === 0 && (
                  <div className="p-8 text-center text-slate-500 font-sans text-sm border-2 border-dashed border-slate-200 rounded-xl">
                    No items found matching your search.
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Item Editor Modal */}
      {editingItem && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-sans text-lg font-bold text-slate-900">
                {editingItem.index !== undefined ? 'Edit Item' : 'New Item'}
              </h3>
              <button onClick={() => setEditingItem(null)} className="text-slate-400 hover:text-slate-700 bg-slate-100 p-1.5 rounded-lg">
                <span className="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-5 custom-scrollbar">
              
              {editingItem.type === 'stats' ? (
                <>
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-600 uppercase mb-2">Number (e.g. 10+)</label>
                    <input type="text" value={editingItem.data.number} onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, number: e.target.value}})} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:border-secondary focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-600 uppercase mb-2">Label</label>
                    <input type="text" value={editingItem.data.label} onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, label: e.target.value}})} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:border-secondary focus:outline-none" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="spanTwo" checked={editingItem.data.spanTwo} onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, spanTwo: e.target.checked}})} className="rounded border-slate-300 text-secondary focus:ring-secondary" />
                    <label htmlFor="spanTwo" className="text-sm font-sans text-slate-700">Span 2 columns on mobile?</label>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-mono font-bold text-slate-600 uppercase mb-2">Title</label>
                      <input type="text" value={editingItem.data.title} onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, title: e.target.value}})} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:border-secondary focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-mono font-bold text-slate-600 uppercase mb-2 flex justify-between">
                        Icon (Material Symbol)
                        <a href="https://fonts.google.com/icons" target="_blank" className="text-secondary hover:underline">Reference</a>
                      </label>
                      <div className="relative">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{editingItem.data.icon}</span>
                        <input type="text" value={editingItem.data.icon} onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, icon: e.target.value}})} className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:border-secondary focus:outline-none" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-mono font-bold text-slate-600 uppercase mb-2">Description</label>
                    <textarea rows="3" value={editingItem.data.description} onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, description: e.target.value}})} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:border-secondary focus:outline-none" />
                  </div>
                  
                  {(editingItem.type === 'engineeringScope' || editingItem.type === 'whyNovatek') && (
                    <div className="space-y-4">
                      {editingItem.type === 'engineeringScope' && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-mono font-bold text-slate-600 uppercase mb-2">Link Text</label>
                            <input type="text" value={editingItem.data.linkText || ''} onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, linkText: e.target.value}})} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:border-secondary focus:outline-none" />
                          </div>
                          <div>
                            <label className="block text-xs font-mono font-bold text-slate-600 uppercase mb-2">Link To (Target Destination)</label>
                            <select
                              value={isCustomLink ? 'custom' : (editingItem.data.linkTo || '')}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === 'custom') {
                                  setIsCustomLink(true);
                                } else {
                                  setIsCustomLink(false);
                                  setEditingItem({
                                    ...editingItem,
                                    data: { ...editingItem.data, linkTo: val }
                                  });
                                }
                              }}
                              className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:border-secondary focus:outline-none font-sans"
                            >
                              <option value="">-- No Link (None) --</option>
                              
                              <optgroup label="Industries">
                                {(dropdownOptions.industries || []).map(ind => (
                                  <option key={`ind-${ind.slug}`} value={`industries:${ind.slug}`}>
                                    Industry: {ind.title}
                                  </option>
                                ))}
                              </optgroup>

                              <optgroup label="Product Categories">
                                {(dropdownOptions.categories || []).map(cat => (
                                  <option key={`cat-${cat.name}`} value={`products:${cat.name}`}>
                                    Category: {cat.name}
                                  </option>
                                ))}
                              </optgroup>

                              <optgroup label="Specific Products">
                                {(dropdownOptions.products || []).map(prod => (
                                  <option key={`prod-${prod.slug}`} value={`product-detail:${prod.slug}`}>
                                    Product: {prod.title}
                                  </option>
                                ))}
                              </optgroup>

                              <option value="custom">-- Custom / Manual Slug --</option>
                            </select>
                            
                            {isCustomLink && (
                              <div className="mt-2 animate-fadeIn">
                                <input
                                  type="text"
                                  placeholder="e.g. solutions:industrial-testing"
                                  value={editingItem.data.linkTo || ''}
                                  onChange={(e) => setEditingItem({
                                    ...editingItem,
                                    data: { ...editingItem.data, linkTo: e.target.value }
                                  })}
                                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:border-secondary focus:outline-none font-mono"
                                />
                                <span className="text-[10px] text-slate-400 mt-1 block">Type raw target string (format: tab:slug)</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-mono font-bold text-slate-600 uppercase mb-2">Cover Image</label>
                        {editingItem.data.image ? (
                          <div className="relative w-full h-32 rounded-xl overflow-hidden border-2 border-slate-200 group">
                            <img src={window.resolveMediaUrl(editingItem.data.image)} alt="Cover" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity gap-3">
                              <button onClick={() => { setMediaPickerTarget('editingItem.image'); setMediaPickerOpen(true); }} className="px-3 py-1.5 bg-white text-slate-900 rounded-lg text-sm font-bold shadow hover:bg-slate-100">Change</button>
                              <button onClick={() => setEditingItem({...editingItem, data: {...editingItem.data, image: ''}})} className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-bold shadow hover:bg-red-600">Remove</button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            onClick={() => { setMediaPickerTarget('editingItem.image'); setMediaPickerOpen(true); }}
                            className="w-full h-32 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 hover:text-secondary hover:border-secondary hover:bg-orange-50 cursor-pointer transition-all"
                          >
                            <span className="material-symbols-outlined text-3xl mb-1">add_photo_alternate</span>
                            <span className="text-sm font-bold">Select Cover Image</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="p-5 border-t border-slate-100 flex justify-end gap-3 bg-slate-50 rounded-b-2xl">
              <button onClick={() => setEditingItem(null)} className="px-5 py-2.5 rounded-xl font-sans text-sm font-bold text-slate-600 hover:bg-slate-200 transition-colors">
                Cancel
              </button>
              <button onClick={handleSaveEdit} className="px-6 py-2.5 rounded-xl font-sans text-sm font-bold text-white bg-secondary hover:bg-orange-600 transition-colors shadow-md">
                Done Editing
              </button>
            </div>
          </div>
        </div>
      )}

    {window.MediaPickerModal && (
      <window.MediaPickerModal
        isOpen={mediaPickerOpen}
        onClose={() => {
          setMediaPickerOpen(false);
          setMediaPickerTarget(null);
        }}
        onSelect={(url) => {
          if (mediaPickerTarget === 'editingItem.image' && editingItem) {
            setEditingItem({
              ...editingItem,
              data: { ...editingItem.data, image: url }
            });
          }
          setMediaPickerOpen(false);
          setMediaPickerTarget(null);
        }}
      />
    )}

    </div>
  );
};
